import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  InputAdornment,
  IconButton,
  OutlinedInput,
  Button
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

const UserInfoTab = ({
  formData,
  handleInputChange,
  selectedUser,
  showPassword,
  setShowPassword,
  teams,
  memberTeamDropdownOpen,
  setMemberTeamDropdownOpen,
  ledTeamsDropdownOpen,
  setLedTeamsDropdownOpen,
  handleCloseDropdown
}) => {
  return (
    <Grid container spacing={2}>
      {/* Left Column */}
      <Grid item xs={12} md={6}>
        <TextField
          name="username"
          label="Username"
          fullWidth
          value={formData.username}
          onChange={handleInputChange}
          disabled={!!selectedUser}
          required
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        />
        <FormControl 
          fullWidth 
          variant="outlined"
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  top: '-2px',
                  '&.MuiInputLabel-shrunk': {
                    top: '-6px',
                  }
                }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        >
          <InputLabel htmlFor="password">Password</InputLabel>
          <OutlinedInput
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password || ''}
            onChange={handleInputChange}
            required={!selectedUser}
            label="Password"
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={selectedUser && formData.password === 'Enter new password to update'}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            }
            placeholder={selectedUser ? "Enter new password to update" : "Enter password"}
          />
        </FormControl>
        
        {/* Extra space after 2nd row */}
        <Box sx={{ 
          mb: 4,
          '@media (max-width:600px)': {
            mb: 1,
          }
        }}></Box>
        
        <TextField
          name="firstName"
          label="First Name"
          fullWidth
          value={formData.firstName}
          onChange={handleInputChange}
          required
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        />
        <TextField
          name="lastName"
          label="Last Name"
          fullWidth
          value={formData.lastName}
          onChange={handleInputChange}
          required
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        />
        
        {/* Extra space after 4th row */}
        <Box sx={{ 
          mb: 4,
          '@media (max-width:600px)': {
            mb: 1,
          }
        }}></Box>
        
        <FormControl 
          fullWidth 
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                top: '-2px',
                '&.MuiInputLabel-shrunk': {
                  top: '-6px',
                }
              },
              '& .MuiSelect-select': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        >
          <InputLabel>Role</InputLabel>
          <Select
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            label="Role"
            required
          >
            {[
              { value: 'admin', label: 'Admin' },
              { value: 'team_lead', label: 'Team Lead' },
              { value: 'user', label: 'User' },
              { value: 'accountant', label: 'Accountant' }
            ].sort((a, b) => a.label.localeCompare(b.label)).map(role => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl 
              fullWidth 
              sx={{
                '@media (max-width:600px)': {
                  margin: 0,
                  marginBottom: '16px',
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                    height: '40px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                    top: '-2px',
                    '&.MuiInputLabel-shrunk': {
                      top: '-6px',
                    }
                  },
                  '& .MuiSelect-select': {
                    padding: '8px 14px',
                  }
                },
                '@media (min-width:601px)': {
                  marginBottom: '16px',
                }
              }}
            >
              <InputLabel>Payroll Type *</InputLabel>
              <Select
                name="payrollType"
                value={formData.payrollType}
                onChange={handleInputChange}
                label="Payroll Type *"
                required
              >
                <MenuItem value="Hourly">Hourly</MenuItem>
                <MenuItem value="Salary">Salary</MenuItem>
                <MenuItem value="Hourly Indirect">Hourly Indirect</MenuItem>
                <MenuItem value="Salary Indirect">Salary Indirect</MenuItem>
                <MenuItem value="N/A">N/A</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl 
              fullWidth 
              sx={{
                '@media (max-width:600px)': {
                  margin: 0,
                  marginBottom: '16px',
                  '& .MuiInputBase-root': {
                    fontSize: '0.875rem',
                    height: '40px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.875rem',
                    top: '-2px',
                    '&.MuiInputLabel-shrunk': {
                      top: '-6px',
                    }
                  },
                  '& .MuiSelect-select': {
                    padding: '8px 14px',
                  }
                },
                '@media (min-width:601px)': {
                  marginBottom: '16px',
                }
              }}
            >
              <InputLabel>Employment Status *</InputLabel>
              <Select
                name="employmentStatus"
                value={formData.employmentStatus}
                onChange={handleInputChange}
                label="Employment Status *"
                required
              >
                <MenuItem value="Full-Time">Full-Time</MenuItem>
                <MenuItem value="Part-Time">Part-Time</MenuItem>
                <MenuItem value="1099">1099</MenuItem>
                <MenuItem value="N/A">N/A</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
      
      {/* Right Column */}
      <Grid item xs={12} md={6}>
        <TextField
          name="email"
          label="Email"
          type="email"
          fullWidth
          value={formData.email}
          onChange={handleInputChange}
          required
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        />
        <TextField
          name="quickbooksName"
          label="Quickbooks Name"
          fullWidth
          value={formData.quickbooksName}
          onChange={handleInputChange}
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        />
        
        {/* Extra space after 2nd row */}
        <Box sx={{ 
          mb: 4,
          '@media (max-width:600px)': {
            mb: 1,
          }
        }}></Box>
        
        <TextField
          name="title"
          label="Title"
          fullWidth
          value={formData.title}
          onChange={handleInputChange}
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        />
        <TextField
          name="dateOfHire"
          label="Date of Hire (YYYY-MM-DD)"
          fullWidth
          value={formData.dateOfHire}
          onChange={handleInputChange}
          placeholder="YYYY-MM-DD"
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px',
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        />
        
        {/* Extra space after 4th row */}
        <Box sx={{ 
          mb: 4,
          '@media (max-width:600px)': {
            mb: 1,
          }
        }}></Box>
        
        <FormControl 
          fullWidth 
          sx={{
            '@media (max-width:600px)': {
              margin: 0,
              marginBottom: '16px',
              '& .MuiInputBase-root': {
                fontSize: '0.875rem',
                height: '40px',
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                top: '-2px',
                '&.MuiInputLabel-shrunk': {
                  top: '-6px',
                }
              }
            },
            '@media (min-width:601px)': {
              marginBottom: '16px',
            }
          }}
        >
          <InputLabel>Member Teams</InputLabel>
          <Select
            name="memberTeam"
            multiple
            value={formData.memberTeam}
            onChange={handleInputChange}
            label="Member Teams"
            open={memberTeamDropdownOpen}
            onOpen={() => setMemberTeamDropdownOpen(true)}
            onClose={() => setMemberTeamDropdownOpen(false)}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300
                },
                onClick: handleCloseDropdown
              }
            }}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <em>None</em>;
              }
              return selected
                .map(id => {
                  const team = teams.find(t => t._id === id);
                  return team ? team.name : '';
                })
                .filter(Boolean)
                .sort((a, b) => a.localeCompare(b))
                .join(', ');
            }}
          >
            {teams
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(team => (
                <MenuItem key={team._id} value={team._id}>
                  {team.name}
                </MenuItem>
              ))
            }
            <Box 
              sx={{ 
                position: 'sticky', 
                bottom: 0, 
                backgroundColor: 'white', 
                borderTop: '1px solid #e0e0e0',
                p: 1,
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <Button 
                variant="contained" 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  setMemberTeamDropdownOpen(false);
                }}
              >
                Done
              </Button>
            </Box>
          </Select>
        </FormControl>
        
        {/* Led Teams (only visible for team_lead role) */}
        {formData.role === 'team_lead' && (
          <FormControl 
            fullWidth 
            sx={{
              '@media (max-width:600px)': {
                margin: 0,
                marginBottom: '16px',
                '& .MuiInputBase-root': {
                  fontSize: '0.875rem',
                  height: '40px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.875rem',
                  top: '-2px',
                  '&.MuiInputLabel-shrunk': {
                    top: '-6px',
                  }
                }
              },
              '@media (min-width:601px)': {
                marginBottom: '16px',
              }
            }}
          >
            <InputLabel>Led Teams</InputLabel>
            <Select
              name="ledTeams"
              multiple
              value={formData.ledTeams}
              onChange={handleInputChange}
              label="Led Teams"
              open={ledTeamsDropdownOpen}
              onOpen={() => setLedTeamsDropdownOpen(true)}
              onClose={() => setLedTeamsDropdownOpen(false)}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300
                  },
                  onClick: handleCloseDropdown
                }
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>None</em>;
                }
                return selected
                  .map(id => {
                    const team = teams.find(t => t._id === id);
                    return team ? team.name : '';
                  })
                  .filter(Boolean)
                  .sort((a, b) => a.localeCompare(b))
                  .join(', ');
              }}
            >
              {teams
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(team => (
                  <MenuItem key={team._id} value={team._id}>
                    {team.name}
                  </MenuItem>
                ))
              }
              <Box 
                sx={{ 
                  position: 'sticky', 
                  bottom: 0, 
                  backgroundColor: 'white', 
                  borderTop: '1px solid #e0e0e0',
                  p: 1,
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <Button 
                  variant="contained" 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    setLedTeamsDropdownOpen(false);
                  }}
                >
                  Done
                </Button>
              </Box>
            </Select>
          </FormControl>
        )}
      </Grid>
    </Grid>
  );
};

export default UserInfoTab;
